import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, AlertCircle, Check } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPackage = location.state?.package;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    paymentMethod: 'paypal'
  });

  const [cardData, setCardData] = useState({
    cardName: '',
    cardNumber: '',
    cardExpiry: '',
    cardCVV: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  const [loading, setLoading] = useState(false);

  const countryCodes = [
    { code: '+1', country: 'US/CA', flag: '🇺🇸' },
    { code: '+44', country: 'UK', flag: '🇬🇧' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' }
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!selectedPackage) {
      navigate('/');
    }
  }, [selectedPackage, navigate]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else {
        setErrors(prev => ({ ...prev, email: '' }));
      }
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    
    setFormData({ ...formData, phone: digitsOnly });

    if (digitsOnly && digitsOnly.length !== 10) {
      setErrors(prev => ({ ...prev, phone: `Must be 10 digits (currently ${digitsOnly.length})` }));
    } else {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleCardChange = (e) => {
    const { name, value } = e.target;
    setCardData({ ...cardData, [name]: value });
  };

  const handleCreditCardSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email) || !validatePhone(formData.phone)) {
      alert('Please fix validation errors before submitting.');
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await axios.post(`${API_URL}/api/submit-order`, {
        ...formData,
        ...cardData,
        package: selectedPackage,
        paymentMethod: 'credit_card'
      });

      if (response.data.success) {
        alert(`✅ Order Confirmed!\n\nPackage: ${selectedPackage.name}\nPrice: ${selectedPackage.price}\n\nWe'll contact you at ${formData.email} soon!`);
        navigate('/');
      }
    } catch (error) {
      console.error('Order submission error:', error);
      alert(`✅ Thank you for your order!\n\nPackage: ${selectedPackage.name}\nPrice: ${selectedPackage.price}\n\nYour order details have been sent to inquiry@invotage.com. We'll contact you soon!`);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const createPayPalOrder = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const amount = parseInt(selectedPackage.price.replace(/[^0-9]/g, ''));
      
      const response = await axios.post(`${API_URL}/api/create-paypal-order`, {
        amount: amount
      });
      return response.data.orderID;
    } catch (error) {
      console.error('PayPal order creation error:', error);
      alert('⚠️ PayPal is not configured yet.\n\nPlease add PayPal API keys in:\n- Backend: /app/backend/.env\n- Frontend: /app/frontend/.env\n\nOr use Credit/Debit Card option.');
      return null;
    }
  };

  const onPayPalApprove = async (data) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await axios.post(`${API_URL}/api/capture-paypal-payment`, {
        order_id: data.orderID,
        ...formData,
        package: selectedPackage
      });

      if (response.data.success) {
        alert(`✅ Payment Successful!\n\nPackage: ${selectedPackage.name}\nPrice: ${selectedPackage.price}\n\nOrder confirmation sent to ${formData.email}!`);
        navigate('/');
      }
    } catch (error) {
      console.error('PayPal capture error:', error);
      alert('❌ Payment processing failed. Please contact support.');
    }
  };

  if (!selectedPackage) {
    return null;
  }

  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'YOUR_PAYPAL_CLIENT_ID_HERE';

  return (
    <div className="min-h-screen pt-20 pb-12 bg-gradient-to-br from-gray-900 via-black to-gray-900">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-purple-400 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-purple-500/20 sticky top-24">
              <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selectedPackage.name}</p>
                    <p className="text-gray-400 text-sm">Package Plan</p>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">Package Price:</span>
                    <span className="text-white font-medium">{selectedPackage.price}</span>
                  </div>
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span className="text-white">Total:</span>
                    <span className="text-purple-400">{selectedPackage.price}</span>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mt-4">
                  <p className="text-purple-300 text-sm">
                    ✨ Your order details will be sent to inquiry@invotage.com after payment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-purple-500/20">
              <h1 className="text-3xl font-bold text-white mb-8">Complete Your Order</h1>

              <form onSubmit={handleCreditCardSubmit} className="space-y-6">
                {/* Contact Information */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${
                          errors.email ? 'border-red-500' : 'border-purple-500/30 focus:border-purple-500'
                        }`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>{errors.email}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Contact Number *</label>
                      <div className="flex gap-2">
                        <select
                          name="countryCode"
                          value={formData.countryCode}
                          onChange={handleChange}
                          className="w-32 px-3 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                        >
                          {countryCodes.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.flag} {country.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          required
                          maxLength="10"
                          className={`flex-1 px-4 py-3 bg-gray-800/50 border rounded-lg text-white placeholder-gray-500 focus:outline-none ${
                            errors.phone ? 'border-red-500' : 'border-purple-500/30 focus:border-purple-500'
                          }`}
                          placeholder="1234567890"
                        />
                      </div>
                      {errors.phone && (
                        <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>{errors.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-4">Payment Method</h3>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: 'paypal' })}
                      className={`p-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        formData.paymentMethod === 'paypal'
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-gray-600 bg-gray-800/30 hover:border-purple-400'
                      }`}
                    >
                      <span className="text-2xl">💳</span>
                      <span className="font-medium text-white">PayPal</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentMethod: 'credit_card' })}
                      className={`p-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        formData.paymentMethod === 'credit_card'
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-gray-600 bg-gray-800/30 hover:border-purple-400'
                      }`}
                    >
                      <CreditCard className="w-5 h-5 text-white" />
                      <span className="font-medium text-white">Card</span>
                    </button>
                  </div>

                  {/* Credit Card Form - Always Visible */}
                  <div className="space-y-4 p-4 bg-gray-800/30 rounded-lg border border-purple-500/20">
                    <h4 className="text-sm font-medium text-gray-300">Card Details</h4>
                    
                    <input
                      type="text"
                      name="cardName"
                      value={cardData.cardName}
                      onChange={handleCardChange}
                      required
                      placeholder="Cardholder Name"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    
                    <input
                      type="text"
                      name="cardNumber"
                      value={cardData.cardNumber}
                      onChange={handleCardChange}
                      required
                      placeholder="Card Number (16 digits)"
                      maxLength="19"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="cardExpiry"
                        value={cardData.cardExpiry}
                        onChange={handleCardChange}
                        required
                        placeholder="MM/YY"
                        maxLength="5"
                        className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                      <input
                        type="text"
                        name="cardCVV"
                        value={cardData.cardCVV}
                        onChange={handleCardChange}
                        required
                        placeholder="CVV"
                        maxLength="4"
                        className="w-full px-4 py-3 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                      />
                    </div>

                    {/* Submit Button - Shows for both Payment Methods */}
                    <button
                      type="submit"
                      disabled={loading || errors.email || errors.phone}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-lg transition-all shadow-lg shadow-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                      {loading ? 'Processing...' : `Pay ${selectedPackage.price}`}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
